import { CfdVideo } from "@/components/CfdVideo";
import { DeviceFrame, useLiveSlot } from "@/components/LiveDevice";
import {
  Paper,
  type PrintJob,
  isKitchen,
  mergeJobs,
  printTime,
  usePrintFeed,
} from "@/components/PrintFeed";
import { cn } from "@/lib/utils";
import type { DemoDevice, EmulatorSessionDTO, FormFactor, PortalDTO } from "@mm-sales/shared";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Copy,
  GripVertical,
  Maximize2,
  Minimize2,
  Moon,
  Pin,
  Play,
  Square,
  X,
} from "lucide-react";
import {
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { SwitchFactsLoader } from "./SwitchFactsLoader";
import { type Brand, money, sampleMenu } from "./data";

/**
 * "All Devices": the whole counter on one screen, no cards — register, handheld, kiosk,
 * customer display and two printers, each at the real emulator's aspect ratio (2560x1600
 * tablet, 1080x2400 phone, the tablet upright for the kiosk). A rep starts any screen on its
 * own emulator, paired as THAT device (its ShopV2 device code is printed under it). The
 * printers are real too: whatever a live POS sends to the kitchen or prints as a receipt
 * comes out of them exactly as the thermal printer would lay it out (PrintFeed). With no
 * live device the mocks loop one order through everything, printouts included.
 */
type Line = { name: string; price: number; qty: number };
export type Role = "register" | "handheld" | "cfd" | "kiosk" | "kds";
export const ROLE_META: Record<
  Role,
  { name: string; sub: string; frame: FormFactor; purpose: string }
> = {
  register: { name: "SUNMI T3 Pro", sub: "POS register", frame: "tablet", purpose: "hw-register" },
  handheld: { name: "SUNMI P3H", sub: "Handheld", frame: "phone", purpose: "hw-handheld" },
  // The kitchen display is a Flex 3 mounted landscape: the same tablet geometry as the register.
  kds: { name: "SUNMI Flex 3", sub: "Kitchen display", frame: "tablet", purpose: "hw-kds" },
  kiosk: {
    name: "SUNMI Flex 3",
    sub: "Kiosk",
    frame: "tablet-portrait",
    purpose: "hw-kiosk",
  },
  cfd: {
    name: "SUNMI T3 Pro",
    sub: "Customer display",
    frame: "tablet",
    purpose: "hw-cfd",
  },
};
const STEPS = 6;
const tax = (n: number) => n * 0.09;
const sum = (ls: Line[]) => ls.reduce((s, l) => s + l.price * l.qty, 0);

/** Devices for one location, by role. Old demos only carry a type: first POS = register, second = handheld. */
function byRole(devices: DemoDevice[]): Partial<Record<Role, DemoDevice>> {
  const out: Partial<Record<Role, DemoDevice>> = {};
  const pos = devices.filter((d) => d.type === "POS");
  for (const d of devices) {
    const role: Role | null =
      d.role === "register" ||
      d.role === "handheld" ||
      d.role === "cfd" ||
      d.role === "kiosk" ||
      d.role === "kds"
        ? d.role
        : d.type === "CFD"
          ? "cfd"
          : d.type === "KDS"
            ? "kds"
            : d.type === "Self Ordering Kiosk"
              ? "kiosk"
              : d.type === "POS"
                ? pos.indexOf(d) === 0
                  ? "register"
                  : pos.indexOf(d) === 1
                    ? "handheld"
                    : null
                : null;
    if (role && !out[role]) out[role] = d;
  }
  return out;
}

function CodeChip({
  code,
  title = "Copy device code",
}: { code: string | undefined; title?: string }) {
  const [copied, setCopied] = useState(false);
  if (!code) return null;
  return (
    <button
      type="button"
      title={title}
      onClick={() => {
        void navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="inline-flex items-center gap-1 rounded-md bg-[#f2f4f8] px-1.5 py-0.5 font-mono text-[11px] tracking-[0.15em] text-[#111318] hover:bg-[#e6e9f0]"
    >
      {code}
      {copied ? (
        <Check className="h-3 w-3 text-[#16a34a]" />
      ) : (
        <Copy className="h-3 w-3 text-[#5b6472]" />
      )}
    </button>
  );
}

/**
 * Front-facing product shots from sunmi.com. `screen` is the glass, as a share of the photo,
 * so the simulator sits in the real display. `aspect` is the photo's width / height.
 */
/** Glass corners as fractions of the photo, in order: top-left, top-right, bottom-right, bottom-left. */
type GlassQuad = readonly (readonly [number, number])[];

const DEVICE_PHOTO: Record<
  Role,
  {
    src: string;
    aspect: number;
    maxWidth?: number;
    screen: React.CSSProperties;
    quad?: GlassQuad;
  }
> = {
  register: {
    // Cashier side of the T3 Pro, from the sunmi.com 3D demo (15.6" + 10.1").
    src: "/hardware/t3-pro-pos.png",
    aspect: 980 / 791,
    screen: { top: "2.2%", left: "2.2%", width: "95.6%", height: "67.6%", borderRadius: 2 },
  },
  handheld: {
    // Straight-on SUNMI P3 family handheld. Glass starts under the printer head.
    src: "/hardware/p3h.png",
    aspect: 928 / 2126,
    maxWidth: 280,
    screen: { top: "13.9%", left: "5.6%", width: "88.8%", height: "84.5%", borderRadius: 18 },
  },
  kiosk: {
    // Portrait Flex 3. The product shot is wider at the bottom, so the glass is a trapezoid.
    src: "/hardware/flex-3-kiosk.png",
    aspect: 620 / 1188,
    maxWidth: 360,
    screen: { top: "8.7%", left: "8.4%", width: "87%", height: "78.1%" },
    quad: [
      [65 / 620, 104 / 1188],
      [577 / 620, 103 / 1188],
      [591 / 620, 1031 / 1188],
      [52 / 620, 1031 / 1188],
    ],
  },
  kds: {
    // Landscape Flex 3. Inset to the glass so the bezel still frames the screen.
    src: "/hardware/flex-3.png",
    aspect: 639 / 380,
    screen: { top: "3.4%", left: "3%", width: "94.2%", height: "91.8%", borderRadius: 8 },
  },
  cfd: {
    // Customer side of the same T3 Pro: the 10.1" display on the back of the main screen.
    src: "/hardware/t3-pro-cfd.png",
    aspect: 980 / 819,
    screen: { top: "17.6%", left: "16.6%", width: "66.8%", height: "48.6%", borderRadius: 2 },
  },
};

/** The Handheld tab can show the P3H or the CPad Pay around the same kind of order screen. */
export type HandheldShell = "p3h" | "cpad";

const CPAD_PHOTO: {
  src: string;
  aspect: number;
  maxWidth: number;
  screen: React.CSSProperties;
  name: string;
  sub: string;
  frame: FormFactor;
  purpose: string;
} = {
  // Straight-on CPad Pay at full size, from the sunmi.com product render.
  src: "/hardware/cpad-pay.png",
  aspect: 1192 / 781,
  maxWidth: 720,
  screen: { top: "8.2%", left: "1.7%", width: "91.3%", height: "89.4%", borderRadius: 12 },
  name: "SUNMI CPad Pay",
  sub: "Handheld",
  frame: "tablet",
  purpose: "hw-cpad",
};

function quadBounds(quad: GlassQuad) {
  let minX = 1;
  let maxX = 0;
  let minY = 1;
  let maxY = 0;
  for (const [x, y] of quad) {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }
  return { minX, maxX, minY, maxY };
}

/** Map the layout box onto the photo's glass. Returns a CSS matrix3d. */
function glassMatrix(w: number, h: number, quad: GlassQuad): string {
  const { minX, maxX, minY, maxY } = quadBounds(quad);
  const spanX = maxX - minX || 1;
  const spanY = maxY - minY || 1;
  const src: ReadonlyArray<readonly [number, number]> = [
    [0, 0],
    [w, 0],
    [w, h],
    [0, h],
  ];
  const m = new Float64Array(8 * 9);
  const at = (r: number, c: number): number => m[r * 9 + c] ?? 0;
  const put = (r: number, c: number, v: number) => {
    m[r * 9 + c] = v;
  };
  quad.forEach(([qx, qy], i) => {
    const from = src[i] ?? [0, 0];
    const x = from[0];
    const y = from[1];
    const u = ((qx - minX) / spanX) * w;
    const v = ((qy - minY) / spanY) * h;
    const r = i * 2;
    put(r, 0, x);
    put(r, 1, y);
    put(r, 2, 1);
    put(r, 6, -u * x);
    put(r, 7, -u * y);
    put(r, 8, u);
    put(r + 1, 3, x);
    put(r + 1, 4, y);
    put(r + 1, 5, 1);
    put(r + 1, 6, -v * x);
    put(r + 1, 7, -v * y);
    put(r + 1, 8, v);
  });
  for (let col = 0; col < 8; col++) {
    let pivot = col;
    for (let r = col + 1; r < 8; r++) {
      if (Math.abs(at(r, col)) > Math.abs(at(pivot, col))) pivot = r;
    }
    if (pivot !== col) {
      for (let c = col; c < 9; c++) {
        const tmp = at(col, c);
        put(col, c, at(pivot, c));
        put(pivot, c, tmp);
      }
    }
    const div = at(col, col) || 1;
    for (let c = col; c < 9; c++) put(col, c, at(col, c) / div);
    for (let r = 0; r < 8; r++) {
      if (r === col) continue;
      const factor = at(r, col);
      for (let c = col; c < 9; c++) put(r, c, at(r, c) - factor * at(col, c));
    }
  }
  const h00 = at(0, 8);
  const h01 = at(1, 8);
  const h02 = at(2, 8);
  const h10 = at(3, 8);
  const h11 = at(4, 8);
  const h12 = at(5, 8);
  const h20 = at(6, 8);
  const h21 = at(7, 8);
  const css = [h00, h10, 0, h20, h01, h11, 0, h21, 0, 0, 1, 0, h02, h12, 0, 1];
  return `matrix3d(${css.map((v) => v.toFixed(5)).join(",")})`;
}

/** Screen that follows a perspective glass instead of a rectangle. */
function SlantedGlass({ quad, children }: { quad: GlassQuad; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [transform, setTransform] = useState("none");
  const { minX, maxX, minY, maxY } = quadBounds(quad);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const apply = () => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      if (w < 2 || h < 2) return;
      const next = glassMatrix(w, h, quad);
      setTransform((cur) => (cur === next ? cur : next));
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [quad]);
  return (
    <div
      ref={ref}
      className="absolute overflow-hidden bg-black"
      style={{
        top: `${minY * 100}%`,
        left: `${minX * 100}%`,
        width: `${(maxX - minX) * 100}%`,
        height: `${(maxY - minY) * 100}%`,
        transform,
        transformOrigin: "0 0",
        borderRadius: 8,
      }}
    >
      <div className="h-full min-h-0 w-full">{children}</div>
    </div>
  );
}

function SunmiShell({
  role,
  shell,
  children,
}: {
  role: Role;
  shell?: HandheldShell;
  children: ReactNode;
}) {
  const photo = role === "handheld" && shell === "cpad" ? CPAD_PHOTO : DEVICE_PHOTO[role];
  const quad = "quad" in photo ? photo.quad : undefined;
  return (
    <div
      className="relative mx-auto w-full"
      style={photo.maxWidth ? { maxWidth: photo.maxWidth } : undefined}
    >
      <img
        src={photo.src}
        alt=""
        draggable={false}
        className="pointer-events-none block h-auto w-full select-none"
      />
      {quad ? (
        <SlantedGlass quad={quad}>{children}</SlantedGlass>
      ) : (
        <div className="absolute overflow-hidden bg-black" style={photo.screen}>
          <div className="h-full min-h-0 w-full">{children}</div>
        </div>
      )}
    </div>
  );
}

/**
 * Shrinks a fixed-width block until it sits inside the glass. Transform does not
 * change layout size, so the slot is sized to the scaled box or the stand clips it.
 */
function FitToScreen({ children }: { children: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState({ scale: 1, w: 340, h: 0 });
  useLayoutEffect(() => {
    const frame = box.current;
    const node = inner.current;
    if (!frame || !node) return;
    const measure = () => {
      const bw = frame.clientWidth;
      const bh = frame.clientHeight;
      const iw = node.scrollWidth;
      const ih = node.scrollHeight;
      if (iw < 2 || ih < 2 || bw < 2 || bh < 2) return;
      const scale = Math.min(1, (bw - 8) / iw, (bh - 8) / ih);
      setFit((cur) =>
        Math.abs(cur.scale - scale) < 0.015 && cur.w === iw && cur.h === ih
          ? cur
          : { scale, w: iw, h: ih },
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(frame);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={box} className="flex h-full w-full items-center justify-center overflow-hidden">
      <div
        className="relative shrink-0"
        style={{ width: fit.w * fit.scale, height: Math.max(fit.h * fit.scale, 1) }}
      >
        <div
          ref={inner}
          className="absolute left-0 top-0 w-[340px]"
          style={{ transform: `scale(${fit.scale})`, transformOrigin: "top left" }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

const BOOT_CARD_W = 340;
const BOOT_CARD_H = 250;

/** Where a boot card can sit beside a device that is too small to hold it. */
function placeBootCard(device: DOMRect, bounds: DOMRect): { left: number; top: number } | null {
  const gap = 16;
  const leftRoom = device.left - bounds.left;
  const rightRoom = bounds.right - device.right;
  const side =
    rightRoom >= leftRoom && rightRoom >= BOOT_CARD_W + gap
      ? "right"
      : leftRoom >= BOOT_CARD_W + gap
        ? "left"
        : rightRoom >= leftRoom && rightRoom >= 300
          ? "right"
          : leftRoom >= 300
            ? "left"
            : null;
  if (!side) return null;
  const left =
    side === "right"
      ? Math.min(device.right + gap, bounds.right - gap - BOOT_CARD_W)
      : Math.max(bounds.left + gap, device.left - gap - BOOT_CARD_W);
  const top = Math.max(
    12,
    Math.min(device.top + (device.height - BOOT_CARD_H) / 2, window.innerHeight - BOOT_CARD_H - 12),
  );
  return { left: Math.round(left), top: Math.round(top) };
}

function bootSpot(frame: HTMLElement): { left: number; top: number } | null {
  const photo = frame.querySelector("img");
  const device = (photo ?? frame).getBoundingClientRect();
  if (device.width < 2) return null;
  const wide = contentFrame(frame);
  let left = wide.left;
  let right = wide.right;
  const tile = frame.closest(".group");
  const row = tile?.parentElement;
  if (row && tile) {
    const kids = [...row.children];
    const index = kids.indexOf(tile);
    const prev = index > 0 ? kids[index - 1]?.getBoundingClientRect() : undefined;
    const next = kids[index + 1]?.getBoundingClientRect();
    if (prev) left = Math.max(left, prev.right);
    if (next) right = Math.min(right, next.left);
  }
  return placeBootCard(device, new DOMRect(left, 0, Math.max(0, right - left), window.innerHeight));
}

function contentFrame(el: HTMLElement): DOMRect {
  const device = el.getBoundingClientRect();
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.body) {
    const r = node.getBoundingClientRect();
    if (r.width >= device.width + 320) return r;
    node = node.parentElement;
  }
  return new DOMRect(0, 0, window.innerWidth, window.innerHeight);
}

/** The comparison card, parked in the open space beside a small device. */
function BesideBootCard({ frame }: { frame: RefObject<HTMLDivElement | null> }) {
  const [spot, setSpot] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const el = frame.current;
    if (!el) return;
    const place = () => {
      const device = el.getBoundingClientRect();
      if (device.bottom < 0 || device.top > window.innerHeight) {
        setSpot(null);
        return;
      }
      const next = bootSpot(el);
      setSpot((cur) =>
        cur && next && cur.left === next.left && cur.top === next.top ? cur : next,
      );
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(el);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [frame]);
  if (!spot) return null;
  return createPortal(
    <div
      className="pointer-events-none fixed z-30 w-[340px] rounded-2xl bg-[#0b1220] px-3 py-3 shadow-2xl"
      style={{ left: spot.left, top: spot.top }}
    >
      <SwitchFactsLoader />
    </div>,
    document.body,
  );
}

/** Dims a screen that has not been started and offers the one way to wake it. */
function SleepingOverlay({
  busy,
  onStart,
  frame,
}: {
  busy: boolean;
  onStart: () => void;
  /** The device photo, so a card that will not fit can sit beside it. */
  frame?: RefObject<HTMLDivElement | null>;
}) {
  const glass = useRef<HTMLDivElement>(null);
  const [beside, setBeside] = useState(false);
  useLayoutEffect(() => {
    if (!busy) return;
    const pane = glass.current;
    const host = frame?.current;
    if (!pane || !host) return;
    const check = () => {
      const readable = pane.clientWidth >= 300 && pane.clientHeight >= 210;
      if (readable) {
        setBeside(false);
        return;
      }
      setBeside(bootSpot(host) != null);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(pane);
    ro.observe(host);
    window.addEventListener("resize", check);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", check);
    };
  }, [busy, frame]);
  if (busy) {
    return (
      <div
        ref={glass}
        className="absolute inset-0 z-10 min-w-0 overflow-hidden bg-[#0b1220]/80 backdrop-blur-[2px]"
      >
        {beside ? (
          <>
            <div className="flex h-full w-full items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/25 border-t-white" />
            </div>
            {frame ? <BesideBootCard frame={frame} /> : null}
          </>
        ) : (
          <FitToScreen>
            <SwitchFactsLoader />
          </FitToScreen>
        )}
      </div>
    );
  }
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-[#0b1220]/55 px-3 text-center backdrop-blur-[1px]">
      <Moon className="h-8 w-8 text-white" strokeWidth={1.75} />
      <p className="text-[14px] font-semibold leading-tight text-white">This device is sleeping</p>
      <button
        type="button"
        onClick={onStart}
        className="inline-flex items-center gap-1.5 rounded-lg bg-[#2563eb] px-3.5 py-2 text-[13px] font-semibold text-white shadow-sm"
      >
        <Play className="h-4 w-4" /> Start Device
      </button>
    </div>
  );
}

/** One screen: the live emulator when the rep started it, else the mock; name + code under it. */
function Screen({
  kind: role,
  device,
  live,
  height,
  children,
  mirror,
  pin,
  shell,
  compact,
}: {
  kind: Role;
  device: DemoDevice | undefined;
  live: ReturnType<typeof useLiveSlot>;
  height: string;
  children: ReactNode;
  /** Employee PIN the POS app asks for right after pairing (register + handheld). */
  pin?: string | null;
  /** Customer display: not its own device — the register's second screen, when the register is live. */
  mirror?: { session: EmulatorSessionDTO | null };
  /** Handheld tab: P3H, or the CPad Pay around the same order screen. */
  shell?: HandheldShell;
  /** Floor tile: one short caption, and a smaller sleeping state. */
  compact?: boolean;
}) {
  const meta = role === "handheld" && shell === "cpad" ? CPAD_PHOTO : ROLE_META[role];
  const running = mirror ? Boolean(mirror.session?.cfdDisplayId != null) : Boolean(live.session);
  const start = () =>
    live.launch("pos", {
      deviceCode: device?.code ?? null,
      formFactor: meta.frame,
      // One emulator, two screens: the register also carries the customer display.
      cfd: role === "register",
    });
  // Reps can wake a screen; prospects keep the looping mock. The customer display
  // has no session of its own — it comes up with the register.
  const asleep = live.enabled && !running && !mirror;
  const frameRef = useRef<HTMLDivElement>(null);
  const endButton =
    live.enabled && running && !mirror ? (
      <button
        type="button"
        onClick={live.end}
        disabled={live.busy}
        className="inline-flex items-center gap-1 rounded-md bg-[#c8102e] px-2 py-0.5 text-[11px] font-semibold text-white disabled:opacity-60"
      >
        <Square className="h-3 w-3" /> End
      </button>
    ) : null;
  return (
    <div className={cn("flex min-w-0 flex-col items-center", compact ? "gap-1" : "gap-2")}>
      <div ref={frameRef} className="w-full">
        <SunmiShell role={role} shell={shell}>
          {running && mirror ? (
            <CfdVideo session={mirror.session} />
          ) : running ? (
            <DeviceFrame bare frame={meta.frame} session={live.session} maxHeight={height} />
          ) : (
            <>
              {children}
              {asleep ? (
                <SleepingOverlay busy={live.busy} onStart={start} frame={frameRef} />
              ) : null}
            </>
          )}
        </SunmiShell>
      </div>
      {compact ? (
        <div className="flex h-9 w-full min-w-0 flex-col items-center justify-center leading-none">
          <div className="max-w-full truncate text-[11px] font-bold">{meta.name}</div>
          <div className="mt-0.5 flex max-w-full items-center gap-1 overflow-hidden">
            {device ? (
              <CodeChip code={device.code} />
            ) : (
              <span className="truncate text-[10px] text-[#5b6472]">{meta.sub}</span>
            )}
            {pin ? <CodeChip code={pin} title="Copy employee PIN" /> : null}
            {endButton}
          </div>
        </div>
      ) : null}
      {compact ? null : (
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center">
          <span className="text-[13px] font-bold">{meta.name}</span>
          <span className="text-[11px] text-[#5b6472]">{meta.sub}</span>
          {device ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-[#5b6472]">
              Device code <CodeChip code={device.code} />
            </span>
          ) : null}
          {pin ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-[#5b6472]">
              Employee PIN <CodeChip code={pin} title="Copy employee PIN" />
            </span>
          ) : null}
          {live.enabled && mirror ? (
            <span className="text-[11px] text-[#5b6472]">
              {running ? "Live · the register's second screen" : "Starts with the register"}
            </span>
          ) : (
            endButton
          )}
        </div>
      )}
    </div>
  );
}

const PIECES = ["register", "cfd", "kitchen", "receipt", "kds", "kiosk", "handheld"] as const;
type PieceId = (typeof PIECES)[number];
type Spot = { x: number; y: number };
type Layout = Record<PieceId, Spot>;

const PIECE_LABEL: Record<PieceId, string> = {
  register: "POS register",
  cfd: "customer display",
  kitchen: "kitchen printer",
  receipt: "receipt printer",
  kds: "kitchen display",
  kiosk: "kiosk",
  handheld: "P3H",
};

const GRID = 24;

/** Grip row above a tile, plus the one-line caption under a screen. Both are on the 24px grid. */
const HANDLE = 24;
const CAPTION = 40;
const GAP = 24;

/**
 * Relative sizes of the counter. The group scales up to fill the window, and stops
 * before the monitors become the old full-bleed size.
 * Widths are multiples of the snap grid so the default row does not collide after snapping.
 */
const FLOOR_W: Record<PieceId, number> = {
  handheld: 144,
  register: 384,
  cfd: 360,
  kiosk: 168,
  kitchen: 216,
  receipt: 216,
  kds: 432,
};

const PRINTER_PHOTO = {
  kitchen: { src: "/hardware/printer-kitchen.png", aspect: 513 / 512 },
  receipt: { src: "/hardware/printer-58.png", aspect: 883 / 792 },
} as const;

/** One row, sized to share the screen. The first is the counter the reps ask for. */
const LAYOUTS: { label: string; pieces: PieceId[] }[] = [
  {
    label: "POS · Customer · Kitchen · Printer",
    pieces: ["register", "cfd", "kds", "kitchen"],
  },
  { label: "POS · Handheld · Receipt", pieces: ["register", "handheld", "receipt"] },
  { label: "Kiosk · Kitchen · Printer", pieces: ["kiosk", "kds", "kitchen"] },
];

const DOCK: { id: PieceId; src: string; label: string; sub: string }[] = [
  { id: "register", src: DEVICE_PHOTO.register.src, label: "Register", sub: "T3 Pro" },
  { id: "cfd", src: DEVICE_PHOTO.cfd.src, label: "Customer", sub: "T3 Pro" },
  { id: "handheld", src: DEVICE_PHOTO.handheld.src, label: "Handheld", sub: "P3H" },
  { id: "kiosk", src: DEVICE_PHOTO.kiosk.src, label: "Kiosk", sub: "Flex 3" },
  { id: "kds", src: DEVICE_PHOTO.kds.src, label: "Kitchen", sub: "Flex 3" },
  { id: "kitchen", src: PRINTER_PHOTO.kitchen.src, label: "Kitchen", sub: "Printer" },
  { id: "receipt", src: PRINTER_PHOTO.receipt.src, label: "Receipt", sub: "Printer" },
];

function loadPins(key: string): PieceId[] {
  try {
    const raw = JSON.parse(localStorage.getItem(key) ?? "[]") as unknown;
    if (!Array.isArray(raw)) return [];
    const known = new Set(DOCK.map((item) => item.id));
    return DOCK.map((item) => item.id).filter((id) => raw.includes(id) && known.has(id));
  } catch {
    return [];
  }
}

const ROW1: PieceId[] = ["handheld", "register", "cfd", "kiosk"];
const ROW1_W = ROW1.reduce((n, id) => n + FLOOR_W[id], 0) + GAP * (ROW1.length - 1);

function floorScale(boardW: number): number {
  return Math.min(1.55, Math.max(0.82, (boardW - 32) / ROW1_W));
}

/** Width and the room the piece needs below its top, for the default arrangement. */
function pieceSize(id: PieceId, boardW: number): { w: number; h: number } {
  const w = Math.max(24, Math.round((FLOOR_W[id] * floorScale(boardW)) / GRID) * GRID);
  if (id === "kitchen" || id === "receipt") {
    const photo = PRINTER_PHOTO[id];
    return { w, h: HANDLE + GAP + Math.round(w / photo.aspect) };
  }
  return { w, h: HANDLE + CAPTION + Math.round(w / DEVICE_PHOTO[id].aspect) };
}

/** Counter on top. Printers sit under the register; the kitchen screen sits under the customer display. */
function defaultLayout(boardW: number): Layout {
  const sizes = Object.fromEntries(PIECES.map((id) => [id, pieceSize(id, boardW)])) as Record<
    PieceId,
    { w: number; h: number }
  >;
  const row1w = ROW1.reduce((n, id) => n + sizes[id].w, 0) + GAP * (ROW1.length - 1);
  let x = Math.max(0, Math.round((boardW - row1w) / 2));
  const rowH = Math.max(...ROW1.map((id) => sizes[id].h));
  const spots = {} as Partial<Layout>;
  for (const id of ROW1) {
    spots[id] = { x, y: rowH - sizes[id].h };
    x += sizes[id].w + GAP;
  }
  const y2 = rowH + GAP;
  const left = spots.handheld?.x ?? 0;
  const registerRight = (spots.register?.x ?? 0) + sizes.register.w;
  const printersW = sizes.kitchen.w + GAP + sizes.receipt.w;
  const printerX = left + Math.max(0, Math.round((registerRight - left - printersW) / 2));
  const cfdLeft = spots.cfd?.x ?? 0;
  const kioskRight = (spots.kiosk?.x ?? 0) + sizes.kiosk.w;
  const kdsX = cfdLeft + Math.max(0, Math.round((kioskRight - cfdLeft - sizes.kds.w) / 2));
  spots.kitchen = { x: printerX, y: y2 };
  spots.receipt = { x: printerX + sizes.kitchen.w + GAP, y: y2 };
  spots.kds = { x: kdsX, y: y2 };
  return spots as Layout;
}

function snapTo(n: number, max?: number): number {
  const s = Math.max(0, Math.round(n / GRID) * GRID);
  if (max === undefined) return s;
  const cap = Math.max(0, Math.floor(max / GRID) * GRID);
  return Math.min(s, cap);
}

function rectsOverlap(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

function overlaps(
  id: PieceId,
  spot: Spot,
  placed: Partial<Layout>,
  sizes: Record<PieceId, { w: number; h: number }>,
): boolean {
  const mine = { x: spot.x, y: spot.y, w: sizes[id].w, h: sizes[id].h };
  for (const other of PIECES) {
    if (other === id) continue;
    const at = placed[other];
    if (!at) continue;
    if (rectsOverlap(mine, { x: at.x, y: at.y, w: sizes[other].w, h: sizes[other].h })) return true;
  }
  return false;
}

/** Snap every piece onto the grid and push one down a row when it would cover another. */
function settle(layout: Layout, boardW: number): Layout {
  const sizes = Object.fromEntries(PIECES.map((id) => [id, pieceSize(id, boardW)])) as Record<
    PieceId,
    { w: number; h: number }
  >;
  const placed: Partial<Layout> = {};
  for (const id of PIECES) {
    const maxX = Math.max(0, boardW - sizes[id].w);
    const x = snapTo(layout[id].x, maxX);
    let y = snapTo(layout[id].y);
    let guard = 0;
    while (overlaps(id, { x, y }, placed, sizes) && guard < 200) {
      y += GRID;
      guard += 1;
    }
    placed[id] = { x, y };
  }
  return placed as Layout;
}

function loadLayout(key: string): Layout | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw) as Partial<Layout>;
    const out = {} as Layout;
    for (const id of PIECES) {
      const spot = data[id];
      if (!spot || typeof spot.x !== "number" || typeof spot.y !== "number") return null;
      out[id] = { x: spot.x, y: spot.y };
    }
    return out;
  } catch {
    return null;
  }
}

/**
 * All Devices, free to rearrange. Each piece keeps its own screen; only its place on the
 * counter is remembered, per demo, in this browser.
 */
function FloorCanvas({
  storageKey,
  pieces,
}: {
  storageKey: string;
  pieces: Record<PieceId, ReactNode>;
}) {
  const boardRef = useRef<HTMLDivElement>(null);
  const nodes = useRef<Partial<Record<PieceId, HTMLDivElement>>>({});
  const settledSaved = useRef(false);
  const [boardW, setBoardW] = useState(0);
  const [custom, setCustom] = useState<Layout | null>(() => loadLayout(storageKey));
  const [lifted, setLifted] = useState<PieceId | null>(null);
  const [stack, setStack] = useState<PieceId[]>([...PIECES]);
  const width = boardW || 1100;
  // A saved arrangement is already snapped. The untouched counter is snapped here, and a
  // piece that would cover another is moved down to the next free row.
  const layout = custom ?? settle(defaultLayout(width), width);

  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;
    const measure = () => setBoardW(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // A layout saved before snapping may cover another piece. Fix that once, at the real width.
  useEffect(() => {
    if (!boardW || settledSaved.current) return;
    settledSaved.current = true;
    setCustom((cur) => {
      if (!cur) return cur;
      const next = settle(cur, boardW);
      const same = PIECES.every((id) => next[id].x === cur[id].x && next[id].y === cur[id].y);
      return same ? cur : next;
    });
  }, [boardW]);

  useEffect(() => {
    if (!custom) {
      localStorage.removeItem(storageKey);
      return;
    }
    localStorage.setItem(storageKey, JSON.stringify(custom));
  }, [custom, storageKey]);

  const height = Math.max(...PIECES.map((id) => layout[id].y + pieceSize(id, width).h)) + 48;

  const startDrag = (id: PieceId, e: ReactPointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const handle = e.currentTarget;
    const origin = layout[id];
    const base = layout;
    const startX = e.clientX;
    const startY = e.clientY;
    const sizeOf = (pid: PieceId, board: number) => {
      const el = nodes.current[pid];
      const est = pieceSize(pid, board);
      return { w: el?.offsetWidth || est.w, h: el?.offsetHeight || est.h };
    };
    handle.setPointerCapture(e.pointerId);
    setLifted(id);
    setStack((s) => [...s.filter((x) => x !== id), id]);
    const boardAtStart = boardRef.current?.clientWidth || width;
    const sizesAtStart = Object.fromEntries(
      PIECES.map((pid) => [pid, sizeOf(pid, boardAtStart)]),
    ) as Record<PieceId, { w: number; h: number }>;
    const maxXAtStart = Math.max(0, boardAtStart - sizesAtStart[id].w);
    let last = { x: snapTo(origin.x, maxXAtStart), y: snapTo(origin.y) };
    let guard = 0;
    while (overlaps(id, last, base, sizesAtStart) && guard < 200) {
      last = { x: last.x, y: last.y + GRID };
      guard += 1;
    }
    const move = (ev: PointerEvent) => {
      const board = boardRef.current?.clientWidth || width;
      const sizes = Object.fromEntries(PIECES.map((pid) => [pid, sizeOf(pid, board)])) as Record<
        PieceId,
        { w: number; h: number }
      >;
      const maxX = Math.max(0, board - sizes[id].w);
      const next = {
        x: snapTo(origin.x + ev.clientX - startX, maxX),
        y: snapTo(origin.y + ev.clientY - startY),
      };
      // Stay on the last free cell. A cell that covers another device is refused.
      if (!overlaps(id, next, base, sizes)) last = next;
      setCustom({ ...base, [id]: last });
    };
    const up = () => {
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", up);
      handle.removeEventListener("pointercancel", up);
      setLifted(null);
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", up);
    handle.addEventListener("pointercancel", up);
  };

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-[12px] text-[#5b6472]">
        <span>Drag a device. It snaps to the grid and cannot cover another.</span>
        {custom ? (
          <button
            type="button"
            onClick={() => setCustom(null)}
            className="font-semibold text-[#2563eb] hover:underline"
          >
            Reset layout
          </button>
        ) : null}
      </div>
      <div
        ref={boardRef}
        className={cn("relative w-full", lifted && "select-none")}
        style={{
          height,
          backgroundImage: lifted
            ? "linear-gradient(to right, rgba(17,19,24,0.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(17,19,24,0.07) 1px, transparent 1px)"
            : undefined,
          backgroundSize: lifted ? `${GRID}px ${GRID}px` : undefined,
        }}
      >
        {PIECES.map((id) => {
          const size = pieceSize(id, width);
          const spot = layout[id];
          return (
            <div
              key={id}
              ref={(el) => {
                if (el) nodes.current[id] = el;
              }}
              className={cn("absolute", lifted === id && "drop-shadow-2xl")}
              style={{
                left: spot.x,
                top: spot.y,
                width: size.w,
                zIndex: stack.indexOf(id) + 1,
              }}
            >
              <button
                type="button"
                aria-label={`Move ${PIECE_LABEL[id]}`}
                title={`Move ${PIECE_LABEL[id]}`}
                onPointerDown={(e) => startDrag(id, e)}
                className="mb-1 flex h-5 w-full cursor-grab items-center justify-center gap-1 rounded text-[10px] font-medium text-[#8b93a1] hover:bg-[#f2f4f8] hover:text-[#111318] active:cursor-grabbing"
              >
                <GripVertical className="h-3.5 w-3.5" /> Move
              </button>
              {pieces[id]}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Torn bottom edge of a receipt. */
const TEAR =
  "polygon(0 0, 100% 0, 100% calc(100% - 3px), 96% 100%, 92% calc(100% - 3px), 88% 100%, 84% calc(100% - 3px), 80% 100%, 76% calc(100% - 3px), 72% 100%, 68% calc(100% - 3px), 64% 100%, 60% calc(100% - 3px), 56% 100%, 52% calc(100% - 3px), 48% 100%, 44% calc(100% - 3px), 40% 100%, 36% calc(100% - 3px), 32% 100%, 28% calc(100% - 3px), 24% 100%, 20% calc(100% - 3px), 16% 100%, 12% calc(100% - 3px), 8% 100%, 4% calc(100% - 3px), 0 100%)";

/**
 * A thermal printer with the whole printout hanging out of it. Live: the newest job of its
 * kind from any live device on the floor (click the paper to read it full size, and page
 * through everything it printed). Mock: the looping demo order's ticket / receipt.
 */
function Printer({
  name,
  sub,
  photo,
  jobs,
  live,
  mockOn,
  mockPaper,
  onOpen,
}: {
  name: string;
  sub: string;
  photo: { src: string; aspect: number };
  jobs: PrintJob[];
  /** A device on the floor is live: only real printouts count. */
  live: boolean;
  mockOn: boolean;
  mockPaper: ReactNode;
  onOpen: () => void;
}) {
  const last = jobs[jobs.length - 1];
  const on = live ? Boolean(last) : mockOn;
  const status = live
    ? last
      ? `Printed ${printTime(last.ts)}`
      : "Ready"
    : mockOn
      ? "Printed"
      : "Idle";
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (!last) return;
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 1500);
    return () => clearTimeout(t);
  }, [last]);
  return (
    <div className="flex min-w-0 flex-col items-center">
      <div className="flex h-6 w-full items-center justify-center gap-1.5 text-center">
        <span className="truncate text-[11px] font-bold">{name}</span>
        <span
          className={cn(
            "inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold",
            on ? "bg-[#e9f9ee] text-[#15803d]" : "bg-[#f2f4f8] text-[#5b6472]",
          )}
          title={sub}
        >
          <span
            className={cn(
              "h-1.5 w-1.5 rounded-full",
              on ? "bg-[#16a34a]" : "bg-[#9aa3b2]",
              flash && "animate-pulse",
            )}
          />
          {status}
        </span>
      </div>
      <div className="w-full" style={{ aspectRatio: photo.aspect }}>
        <img
          src={photo.src}
          alt=""
          draggable={false}
          className="pointer-events-none h-full w-full select-none object-contain"
        />
      </div>
      {on ? (
        <button
          type="button"
          onClick={onOpen}
          disabled={!(live && last)}
          title={live && last ? "Read the printout" : undefined}
          className={cn(
            "relative z-10 -mt-3 w-[68%] max-w-[240px] cursor-default rounded-b-sm border border-t-0 border-[#e6e9f0] bg-white px-3 py-2 text-left shadow-md",
            live && last && "cursor-zoom-in",
          )}
          style={{ clipPath: TEAR }}
        >
          {live && last ? <Paper job={last} /> : mockPaper}
        </button>
      ) : null}
    </div>
  );
}

/** Full-size view of a printer's output, one printout at a time, newest last. */
function PaperModal({
  title,
  jobs,
  index,
  onIndex,
  onClose,
}: {
  title: string;
  jobs: PrintJob[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && index > 0) onIndex(index - 1);
      if (e.key === "ArrowRight" && index < jobs.length - 1) onIndex(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, jobs.length, onIndex, onClose]);
  const job = jobs[index];
  if (!job) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-[420px] max-w-full flex-col rounded-xl bg-[#1c1f26] p-3 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between gap-2 text-[12px] text-white">
          <span className="font-semibold">
            {title} · {printTime(job.ts)}
            {job.purpose ? (
              <span className="ml-2 font-normal text-white/60">
                from the {job.purpose.replace("hw-", "")}
              </span>
            ) : null}
          </span>
          <span className="flex items-center gap-1">
            <button
              type="button"
              className="rounded p-1 hover:bg-white/10 disabled:opacity-30"
              disabled={index <= 0}
              onClick={() => onIndex(index - 1)}
              title="Earlier printout"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="tabular-nums text-white/70">
              {index + 1}/{jobs.length}
            </span>
            <button
              type="button"
              className="rounded p-1 hover:bg-white/10 disabled:opacity-30"
              disabled={index >= jobs.length - 1}
              onClick={() => onIndex(index + 1)}
              title="Later printout"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="ml-1 rounded p-1 hover:bg-white/10"
              onClick={onClose}
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </span>
        </div>
        <div className="min-h-0 overflow-y-auto">
          <div
            className="mx-auto w-[384px] max-w-full bg-white px-4 py-3 shadow-md"
            style={{ clipPath: TEAR }}
          >
            <Paper job={job} />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * `only`: render just that screen (the category tabs: POS Terminals → register, Handheld →
 * handheld, Kiosks → kiosk, Kitchen Display → KDS), live-capable like on the floor.
 */
export function HardwareFloor({
  b,
  d,
  only,
  shell,
}: {
  b: Brand;
  d: PortalDTO;
  only?: Role;
  /** Which handheld chassis to wrap the order screen in. The floor itself is the P3H. */
  shell?: HandheldShell;
}) {
  const menu = b.menu.length ? b.menu : sampleMenu(b.cuisine);
  const items = useMemo(() => menu.flatMap((g) => g.items).filter((i) => i.price > 0), [menu]);
  const pick = (i: number, qty = 1): Line => {
    const it = items[i % Math.max(items.length, 1)];
    return it ? { name: it.name, price: it.price, qty } : { name: "House special", price: 12, qty };
  };
  const table = [pick(0), pick(2), pick(4, 2)];
  const kiosk = [pick(1), pick(3)];
  // The mocks loop one order through every device: handheld → POS + CFD → kitchen printer →
  // kiosk → cash: receipt.
  const [step, setStep] = useState(1);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % STEPS), 3000);
    return () => clearInterval(t);
  }, []);
  const tableSent = step >= 2;
  const kioskSent = step >= 4;
  const paid = step >= 5;
  const tableTotal = sum(table) + tax(sum(table));

  const all = d.demo.devices ?? [];
  const locations = [...new Set(all.map((x) => x.location))];
  const [loc, setLoc] = useState<string | null>(null);
  const current = loc && locations.includes(loc) ? loc : locations[0];
  const dev = byRole(all.filter((x) => x.location === current));

  const register = useLiveSlot(ROLE_META.register.purpose);
  const handheld = useLiveSlot(ROLE_META.handheld.purpose);
  const cpad = useLiveSlot(CPAD_PHOTO.purpose);
  const kioskLive = useLiveSlot(ROLE_META.kiosk.purpose);
  const cfd = useLiveSlot(ROLE_META.cfd.purpose);
  const kds = useLiveSlot(ROLE_META.kds.purpose);
  const anyError = register.error || handheld.error || cpad.error || kioskLive.error || kds.error;

  // Every POS-mode device can print; the floor's two printers show it all.
  const anyLive = Boolean(
    register.session || handheld.session || cpad.session || kioskLive.session,
  );
  const feed = mergeJobs(
    usePrintFeed(register.session),
    usePrintFeed(handheld.session),
    usePrintFeed(cpad.session),
    usePrintFeed(kioskLive.session),
  );
  // Kitchen display mock: tickets arrive as the loop sends orders; the table one bumps when paid.
  const kdsTickets = [
    ...(tableSent ? [{ title: "Table 4", who: "Alex", ls: table, done: paid, n: 11 }] : []),
    ...(kioskSent
      ? [{ title: "Kiosk #12", who: "Self-order", ls: kiosk, done: false, n: 12 }]
      : []),
  ];
  const kitchenJobs = feed.filter(isKitchen);
  const receiptJobs = feed.filter((j) => !isKitchen(j));
  const [modal, setModal] = useState<{ which: "kitchen" | "receipt"; index: number } | null>(null);
  const [focus, setFocus] = useState<PieceId>("register");
  const [pickerOpen, setPickerOpen] = useState(true);
  const pinKey = `mm-sales:pins:${d.demo.id}`;
  const [pins, setPins] = useState<PieceId[]>(() => loadPins(pinKey));
  useEffect(() => {
    localStorage.setItem(pinKey, JSON.stringify(pins));
  }, [pinKey, pins]);
  const orderPieces = (ids: PieceId[]) =>
    DOCK.map((item) => item.id).filter((item) => ids.includes(item));
  const toggleOnScreen = (id: PieceId) => {
    setExpanded(null);
    const base = pins.length ? pins : [focus];
    const next = base.includes(id) ? base.filter((item) => item !== id) : [...base, id];
    const ordered = orderPieces(next);
    if (ordered.length <= 1) {
      setFocus(ordered[0] ?? id);
      setPins([]);
      return;
    }
    setPins(ordered);
  };
  const applyLayout = (pieces: PieceId[]) => {
    const lineup = pins.length ? pins : [focus];
    const same = lineup.length === pieces.length && pieces.every((id) => lineup.includes(id));
    setExpanded(null);
    setFocus(pieces[0] ?? "register");
    setPins(orderPieces(pieces));
    setPickerOpen(same ? (open) => !open : false);
  };
  const shown = pins.length ? pins : [focus];
  const [expanded, setExpanded] = useState<PieceId | null>(null);
  const stage = expanded && shown.includes(expanded) ? [expanded] : shown;
  const cramped = stage.length > 1;
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpanded(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);
  const modalJobs = modal?.which === "kitchen" ? kitchenJobs : receiptJobs;

  const Lines = ({ ls, dim }: { ls: Line[]; dim?: boolean }) => (
    <ul className={cn("space-y-1 text-[10px]", dim && "opacity-40")}>
      {ls.map((l) => (
        <li key={l.name} className="flex justify-between gap-2">
          <span className="truncate">
            {l.qty}× {l.name}
          </span>
          <span className="shrink-0 tabular-nums">{money(l.price * l.qty)}</span>
        </li>
      ))}
    </ul>
  );
  const now = new Date();
  const stamp = `${now.getMonth() + 1}/${now.getDate()} ${now.getHours() % 12 || 12}:${String(now.getMinutes()).padStart(2, "0")} ${now.getHours() >= 12 ? "PM" : "AM"}`;
  const Dashes = () => <div className="my-0.5 border-t border-dashed border-[#c9d0dc]" />;
  const mockCls = "font-mono text-[11px] leading-[15px] text-[#111318]";
  const kitchenTicket = (title: string, ls: Line[], who: string) => (
    <div className={mockCls}>
      <div className="text-center text-[12px] font-bold">{b.name.toUpperCase()}</div>
      <div className="text-center font-bold">KITCHEN</div>
      <Dashes />
      <div className="flex justify-between">
        <span>{title}</span>
        <span>#{kioskSent && title.startsWith("KIOSK") ? "12" : "11"}</span>
      </div>
      <div className="flex justify-between">
        <span>{who}</span>
        <span>{stamp}</span>
      </div>
      <Dashes />
      {ls.map((l) => (
        <div key={l.name} className="py-0.5 font-bold">
          {l.qty} {l.name}
        </div>
      ))}
      <Dashes />
      <div className="text-center">{ls.reduce((n, l) => n + l.qty, 0)} ITEMS</div>
    </div>
  );
  const receipt = (ls: Line[]) => {
    const sub = sum(ls);
    return (
      <div className={mockCls}>
        <div className="text-center text-[12px] font-bold">{b.name.toUpperCase()}</div>
        <div className="text-center">{b.address || `${b.city}, ${b.state}`}</div>
        <div className="text-center">{b.phone}</div>
        <Dashes />
        <div className="flex justify-between">
          <span>Order #11</span>
          <span>{stamp}</span>
        </div>
        <div className="flex justify-between">
          <span>Table 4</span>
          <span>Dine in</span>
        </div>
        <Dashes />
        {ls.map((l) => (
          <div key={l.name} className="flex justify-between gap-1">
            <span className="truncate">
              {l.qty}x {l.name}
            </span>
            <span className="shrink-0">{money(l.price * l.qty)}</span>
          </div>
        ))}
        <Dashes />
        <div className="flex justify-between">
          <span>Subtotal</span>
          <span>{money(sub)}</span>
        </div>
        <div className="flex justify-between">
          <span>Tax 9%</span>
          <span>{money(tax(sub))}</span>
        </div>
        <div className="flex justify-between font-bold">
          <span>TOTAL</span>
          <span>{money(sub + tax(sub))}</span>
        </div>
        <div className="flex justify-between">
          <span>Cash</span>
          <span>{money(Math.ceil((sub + tax(sub)) / 10) * 10)}</span>
        </div>
        <div className="flex justify-between">
          <span>Change</span>
          <span>{money(Math.ceil((sub + tax(sub)) / 10) * 10 - (sub + tax(sub)))}</span>
        </div>
        <Dashes />
        <div className="text-center">Thank you! Come again.</div>
        <div className="text-center text-[#5b6472]">Powered by MyManager</div>
      </div>
    );
  };

  const registerMock = (
    <div className="flex h-full bg-[#f7f8fb] text-[11px]">
      <div className="w-[20%] min-w-[84px] space-y-1 bg-[#0f172a] p-2 text-white/80">
        <div className="truncate px-1 text-[10px] font-bold uppercase text-white">{b.name}</div>
        {["Home", "Orders", "Tables", "Menu", "Payments", "Reports"].map((l, i) => (
          <div key={l} className={cn("rounded px-1.5 py-1", i === 1 && "bg-white/10 text-white")}>
            {l}
          </div>
        ))}
      </div>
      <div className="flex min-w-0 flex-1 gap-2 p-2">
        <div
          className={cn(
            "flex-1 rounded-lg border bg-white p-2",
            tableSent ? "border-[#2563eb]" : "border-[#e6e9f0]",
          )}
        >
          <div className="mb-1 flex items-center justify-between">
            <span className="font-bold">Table 4</span>
            <span
              className={cn(
                "rounded-full px-1.5 text-[9px] font-semibold",
                paid
                  ? "bg-[#e9f9ee] text-[#15803d]"
                  : tableSent
                    ? "bg-[#eaf0ff] text-[#2563eb]"
                    : "text-[#9aa3b2]",
              )}
            >
              {paid ? "Paid · cash" : tableSent ? "In kitchen" : "—"}
            </span>
          </div>
          {tableSent ? (
            <>
              <Lines ls={table} />
              <div className="mt-1 flex justify-between border-t border-[#e6e9f0] pt-1 font-bold">
                <span>Total</span>
                <span>{money(tableTotal)}</span>
              </div>
            </>
          ) : (
            <div className="text-[#9aa3b2]">Waiting for the handheld…</div>
          )}
        </div>
        <div
          className={cn(
            "flex-1 rounded-lg border bg-white p-2",
            kioskSent ? "border-[#2563eb]" : "border-[#e6e9f0]",
          )}
        >
          <div className="mb-1 flex items-center justify-between">
            <span className="font-bold">Kiosk #12</span>
            <span
              className={cn(
                "rounded-full px-1.5 text-[9px] font-semibold",
                kioskSent ? "bg-[#e9f9ee] text-[#15803d]" : "text-[#9aa3b2]",
              )}
            >
              {kioskSent ? "Paid · card" : "—"}
            </span>
          </div>
          {kioskSent ? (
            <Lines ls={kiosk} />
          ) : (
            <div className="text-[#9aa3b2]">No kiosk orders yet</div>
          )}
        </div>
      </div>
    </div>
  );
  const cfdMock = (
    <div className="flex h-full flex-col bg-white p-3 text-[11px]">
      {!tableSent ? (
        <div className="flex h-full flex-col items-center justify-center text-center">
          <div className="text-[14px] font-bold">{b.name}</div>
          <div className="text-[10px] text-[#5b6472]">Welcome</div>
        </div>
      ) : paid ? (
        <div className="flex h-full flex-col items-center justify-center text-center">
          <div className="text-[15px] font-bold" style={{ color: b.color }}>
            Thank you!
          </div>
          <div className="text-[10px] text-[#5b6472]">{money(tableTotal)} paid in cash</div>
          <div className="mt-1 text-[9px] text-[#5b6472]">Receipt printing…</div>
        </div>
      ) : (
        <>
          <div className="mb-1 font-bold">Your order</div>
          <Lines ls={table} />
          <div className="mt-auto flex justify-between border-t border-[#e6e9f0] pt-1 font-bold">
            <span>Total</span>
            <span>{money(tableTotal)}</span>
          </div>
        </>
      )}
    </div>
  );
  const kioskMock = (
    <div className="flex h-full flex-col bg-white text-[10px]">
      <div
        className="px-3 py-2 text-[12px] font-bold"
        style={{ background: b.color, color: b.palette.onPrimary }}
      >
        Order here
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-2 content-start gap-1.5 overflow-hidden p-2">
        {items.slice(0, 6).map((it) => (
          <div key={it.name} className="rounded-md bg-[#f2f4f8] p-1.5 leading-tight">
            <div className="truncate font-semibold">{it.name}</div>
            <div className="text-[#5b6472]">{money(it.price)}</div>
          </div>
        ))}
      </div>
      <div className="shrink-0 border-t border-[#e6e9f0] p-2">
        {kioskSent ? (
          <>
            <Lines ls={kiosk} />
            <div
              className="mt-1.5 rounded-md py-1.5 text-center font-bold"
              style={{ background: b.color, color: b.palette.onPrimary }}
            >
              Paid {money(sum(kiosk) + tax(sum(kiosk)))}
            </div>
          </>
        ) : (
          <div className="text-[#9aa3b2]">Tap items to start your order</div>
        )}
      </div>
    </div>
  );
  const handheldMock = (
    <div className="flex h-full flex-col bg-white text-[9px]">
      <div
        className="px-2 py-1.5 text-[10px] font-bold"
        style={{ background: b.color, color: b.palette.onPrimary }}
      >
        Table 4
      </div>
      <div className="flex-1 p-2">
        {step >= 1 ? (
          <Lines ls={table} dim={tableSent} />
        ) : (
          <div className="text-[#9aa3b2]">Tap items…</div>
        )}
      </div>
      <div
        className={cn(
          "m-1.5 rounded py-1.5 text-center text-[9px] font-bold text-white",
          tableSent ? "bg-[#16a34a]" : step >= 1 ? "bg-[#2563eb]" : "bg-[#c9d0dc]",
        )}
      >
        {tableSent ? "Sent to kitchen" : "Send to kitchen"}
      </div>
    </div>
  );

  const kdsScreen = (height: string, compact = false) => (
    <Screen kind="kds" device={dev.kds} live={kds} height={height} compact={compact}>
      <div className="flex h-full flex-col bg-[#0f172a] text-white">
        <div className="flex items-center justify-between px-3 py-1.5 text-[10px]">
          <span className="font-bold uppercase tracking-wide">{b.name} · Kitchen</span>
          <span className="text-white/60">
            {kdsTickets.filter((t) => !t.done).length} open · {stamp}
          </span>
        </div>
        <div className="grid min-h-0 flex-1 grid-cols-4 gap-1.5 px-2 pb-2">
          {kdsTickets.map((t) => (
            <div
              key={t.n}
              className={cn(
                "flex min-h-0 flex-col overflow-hidden rounded-md bg-white text-[#111318]",
                t.done && "opacity-40",
              )}
            >
              <div
                className={cn(
                  "flex items-center justify-between px-2 py-1 text-[9px] font-bold text-white",
                  t.done ? "bg-[#16a34a]" : "bg-[#2563eb]",
                )}
              >
                <span>
                  #{t.n} {t.title}
                </span>
                <span className="font-normal opacity-80">{t.who}</span>
              </div>
              <ul className="min-h-0 flex-1 space-y-0.5 overflow-hidden p-2 text-[9px]">
                {t.ls.map((l) => (
                  <li key={l.name} className="flex gap-1">
                    <span className="font-bold">{l.qty}</span>
                    <span className="truncate">{l.name}</span>
                  </li>
                ))}
              </ul>
              <div
                className={cn(
                  "shrink-0 px-2 py-1 text-center text-[8px] font-bold",
                  t.done ? "bg-[#e9f9ee] text-[#15803d]" : "bg-[#f2f4f8] text-[#5b6472]",
                )}
              >
                {t.done ? "Done" : "Bump"}
              </div>
            </div>
          ))}
          {!kdsTickets.length ? (
            <div className="col-span-4 flex items-center justify-center text-[10px] text-white/50">
              Waiting for orders…
            </div>
          ) : null}
        </div>
      </div>
    </Screen>
  );

  if (only) {
    const h = only === "handheld" ? "min(60vh, 34rem)" : "min(56vh, 36rem)";
    return (
      <div className="space-y-3">
        {register.enabled && register.capacity ? (
          <div className="text-[12px] text-[#5b6472]">
            {register.capacity.free}/{register.capacity.total} devices free — start this screen on
            its own emulator.
          </div>
        ) : null}
        {anyError ? <div className="text-[12px] text-[#c8102e]">{anyError}</div> : null}
        <div className="mx-auto max-w-4xl">
          {only === "kds" ? (
            kdsScreen(h)
          ) : only === "register" ? (
            <Screen
              kind="register"
              device={dev.register}
              live={register}
              pin={d.demo.posPin}
              height={h}
            >
              {registerMock}
            </Screen>
          ) : only === "handheld" ? (
            <Screen
              kind="handheld"
              shell={shell}
              device={dev.handheld}
              live={shell === "cpad" ? cpad : handheld}
              pin={d.demo.posPin}
              height={shell === "cpad" ? "min(52vh, 28rem)" : h}
            >
              {handheldMock}
            </Screen>
          ) : only === "kiosk" ? (
            <Screen kind="kiosk" device={dev.kiosk} live={kioskLive} height={h}>
              {kioskMock}
            </Screen>
          ) : (
            <Screen
              kind="cfd"
              device={dev.cfd}
              live={cfd}
              mirror={{ session: register.session }}
              height={h}
            >
              {cfdMock}
            </Screen>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {locations.length > 1 || (register.enabled && register.capacity) ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-[12px] text-[#5b6472]">
          <span>
            {register.enabled && register.capacity
              ? `${register.capacity.free}/${register.capacity.total} devices free — start any screen on its own emulator.`
              : ""}
          </span>
          {locations.length > 1 ? (
            <label className="inline-flex items-center gap-2">
              Device codes for
              <select
                className="rounded-md border border-[#e6e9f0] bg-white px-2 py-1 text-[12px] text-[#111318]"
                value={current}
                onChange={(e) => setLoc(e.target.value)}
              >
                {locations.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      ) : null}
      {anyError ? <div className="text-center text-[12px] text-[#c8102e]">{anyError}</div> : null}

      <div>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-[#8b93a1]">
            Arrange
          </span>
          {LAYOUTS.map((layout) => {
            const on =
              shown.length === layout.pieces.length &&
              layout.pieces.every((id) => shown.includes(id));
            return (
              <button
                key={layout.label}
                type="button"
                aria-pressed={on}
                onClick={() => applyLayout(layout.pieces)}
                className={cn(
                  "rounded-full border px-3 py-1 text-[12px] font-semibold",
                  on
                    ? "border-[#2563eb] bg-[#2563eb] text-white"
                    : "border-[#e6e9f0] bg-white text-[#111318] hover:bg-[#f7f8fb]",
                )}
              >
                {layout.label}
              </button>
            );
          })}
          <button
            type="button"
            aria-expanded={pickerOpen}
            onClick={() => setPickerOpen((open) => !open)}
            className="inline-flex items-center gap-1 rounded-full border border-[#e6e9f0] bg-white px-2.5 py-1 text-[12px] font-semibold text-[#5b6472] hover:text-[#111318]"
          >
            <ChevronUp
              className={cn(
                "h-3.5 w-3.5 transition-transform duration-300",
                !pickerOpen && "rotate-180",
              )}
            />
            Devices
          </button>
        </div>
        <div
          className={cn(
            "grid transition-[grid-template-rows,margin] duration-300 ease-out",
            pickerOpen ? "mb-4 grid-rows-[1fr]" : "mb-0 grid-rows-[0fr]",
          )}
        >
          <div className="min-h-0 overflow-hidden">
            <div
              className={cn(
                "flex gap-2 overflow-x-auto pb-1 transition-transform duration-300 ease-out",
                pickerOpen ? "translate-y-0" : "-translate-y-3",
              )}
            >
              {DOCK.map((item) => {
                const onScreen = shown.includes(item.id);
                return (
                  <div
                    key={item.id}
                    className={cn(
                      "relative flex w-[108px] shrink-0 flex-col rounded-xl border bg-white",
                      onScreen ? "border-[#2563eb] ring-2 ring-[#2563eb]/15" : "border-[#e6e9f0]",
                    )}
                  >
                    <button
                      type="button"
                      aria-label={
                        onScreen ? `Remove ${item.label} from this screen` : `Add ${item.label}`
                      }
                      aria-pressed={onScreen}
                      title={onScreen ? "Remove from this screen" : "Add to this screen"}
                      onClick={() => toggleOnScreen(item.id)}
                      className="absolute right-1 top-1 rounded-md p-1 text-[#8b93a1] hover:bg-[#f2f4f8] hover:text-[#111318]"
                    >
                      <Pin
                        className={cn("h-3.5 w-3.5", onScreen && "fill-[#2563eb] text-[#2563eb]")}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleOnScreen(item.id)}
                      className="flex flex-col items-center px-2 pb-2 pt-2 text-center hover:bg-[#f7f8fb]"
                    >
                      <span className="flex h-14 w-full items-center justify-center">
                        <img src={item.src} alt="" className="max-h-14 w-full object-contain" />
                      </span>
                      <span className="mt-1 text-[12px] font-bold leading-tight">{item.label}</span>
                      <span className="text-[10px] text-[#5b6472]">{item.sub}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {expanded ? (
          <div className="mb-3 flex justify-end">
            <button
              type="button"
              onClick={() => setExpanded(null)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#e6e9f0] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#111318] shadow-sm hover:bg-[#f7f8fb]"
            >
              <Minimize2 className="h-3.5 w-3.5" />
              Show together
            </button>
          </div>
        ) : null}
        <div
          className={cn(
            "flex items-start",
            cramped ? "w-full flex-nowrap gap-3" : "flex-wrap justify-center gap-x-10 gap-y-8",
          )}
        >
          {stage.map((id) => (
            <div
              key={id}
              className={cn(
                "group relative min-w-0",
                !cramped &&
                  (id === "kitchen" || id === "receipt"
                    ? "w-full max-w-[280px]"
                    : id === "handheld"
                      ? "w-full max-w-[280px]"
                      : id === "kiosk"
                        ? "w-full max-w-[400px]"
                        : "w-full max-w-4xl"),
              )}
              style={cramped ? { flex: `${FLOOR_W[id]} 1 0%` } : undefined}
            >
              {cramped ? (
                <button
                  type="button"
                  title="Enlarge"
                  aria-label={`Enlarge ${PIECE_LABEL[id]}`}
                  onClick={() => setExpanded(id)}
                  className="pointer-events-none absolute right-1 top-1 z-20 inline-flex items-center gap-1 rounded-md border border-[#e6e9f0] bg-white/95 px-1.5 py-0.5 text-[10px] font-semibold text-[#5b6472] opacity-0 shadow-sm transition-opacity hover:text-[#111318] group-hover:pointer-events-auto group-hover:opacity-100"
                >
                  <Maximize2 className="h-3 w-3" />
                  Enlarge
                </button>
              ) : null}
              {id === "register" ? (
                <Screen
                  kind="register"
                  device={dev.register}
                  live={register}
                  pin={d.demo.posPin}
                  height={cramped ? "min(42vh, 28rem)" : "min(70vh, 44rem)"}
                >
                  {registerMock}
                </Screen>
              ) : null}
              {id === "cfd" ? (
                <Screen
                  kind="cfd"
                  device={dev.cfd}
                  live={cfd}
                  mirror={{ session: register.session }}
                  height={cramped ? "min(42vh, 28rem)" : "min(70vh, 44rem)"}
                >
                  {cfdMock}
                </Screen>
              ) : null}
              {id === "handheld" ? (
                <Screen
                  kind="handheld"
                  shell="p3h"
                  device={dev.handheld}
                  live={handheld}
                  pin={d.demo.posPin}
                  height={cramped ? "min(48vh, 30rem)" : "min(70vh, 40rem)"}
                >
                  {handheldMock}
                </Screen>
              ) : null}
              {id === "kiosk" ? (
                <Screen
                  kind="kiosk"
                  device={dev.kiosk}
                  live={kioskLive}
                  height={cramped ? "min(48vh, 30rem)" : "min(78vh, 48rem)"}
                >
                  {kioskMock}
                </Screen>
              ) : null}
              {id === "kds" ? kdsScreen(cramped ? "min(42vh, 28rem)" : "min(70vh, 44rem)") : null}
              {id === "kitchen" ? (
                <Printer
                  name="Kitchen"
                  sub="ticket printer"
                  photo={PRINTER_PHOTO.kitchen}
                  jobs={kitchenJobs}
                  live={anyLive}
                  mockOn={tableSent}
                  mockPaper={
                    kioskSent
                      ? kitchenTicket("KIOSK #12", kiosk, "Self-order")
                      : kitchenTicket("TABLE 4", table, "Server: Alex")
                  }
                  onOpen={() => setModal({ which: "kitchen", index: kitchenJobs.length - 1 })}
                />
              ) : null}
              {id === "receipt" ? (
                <Printer
                  name="Receipt"
                  sub="customer copy"
                  photo={PRINTER_PHOTO.receipt}
                  jobs={receiptJobs}
                  live={anyLive}
                  mockOn={paid}
                  mockPaper={receipt(table)}
                  onOpen={() => setModal({ which: "receipt", index: receiptJobs.length - 1 })}
                />
              ) : null}
            </div>
          ))}
        </div>
      </div>

      {anyLive && !feed.length ? (
        <div className="text-center text-[11px] text-[#5b6472]">
          Send an order to the kitchen or print a receipt on a live device — it comes out here.
        </div>
      ) : null}

      {modal && modalJobs.length ? (
        <PaperModal
          title={modal.which === "kitchen" ? "Kitchen printer" : "Receipt printer"}
          jobs={modalJobs}
          index={Math.min(modal.index, modalJobs.length - 1)}
          onIndex={(index) => setModal({ which: modal.which, index })}
          onClose={() => setModal(null)}
        />
      ) : null}
    </div>
  );
}
